import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type {
    FindingPlanumDocType,
    FindingProfileDocType,
    FindingType
} from '@ast-data/shared';
import FindingTypeChip from '../findings/FindingTypeChip';
import FieldFindingCardDescriptions from './FieldFindingCardDescriptions';
import FieldCardPreview from './FieldCardPreview';

/**
 * One Befund on the field Befunde list: a header that opens the Befund editor,
 * over its Planum/Profil descriptions.
 *
 * `memo` is load-bearing rather than a micro-optimisation: the list renders
 * this inside a virtualised grid whose rows are rebuilt on every keystroke and
 * every RxDB emission. The props are kept flat and value-stable for that reason
 * — the page hands the SAME empty array to every Befund without descriptions.
 */
function FieldFindingCard({
    projectId,
    findingId,
    findingNumber,
    findingType,
    shortDescription,
    interpretation,
    preliminaryDating,
    planums,
    profiles,
    isDuplicate,
    previewMediaId,
    previewVersion
}: {
    projectId: string;
    findingId: string;
    findingNumber: string;
    /** Befundbeschreibung or baulicher Bestand — a plain string, so `memo` holds. */
    findingType: FindingType;
    shortDescription: string;
    /** The card's second line, as two plain strings so `memo` holds. */
    interpretation: string;
    preliminaryDating: string;
    planums: FindingPlanumDocType[];
    profiles: FindingProfileDocType[];
    /** The number collides with another Befund in the family. */
    isDuplicate: boolean;
    /** The picture the card shows, '' for none. */
    previewMediaId: string;
    previewVersion: string;
}) {
    const intl = useIntl();
    const navigate = useNavigate();
    // The Kurzbezeichnung already sits in the title line; the second line says
    // what the Befund is taken to be and when — the pair a reader scans for.
    const subtitle = [interpretation, preliminaryDating]
        .map((value) => value.trim())
        .filter(Boolean)
        .join(' - ');

    return (
        <Card variant="outlined">
            <CardActionArea
                onClick={() =>
                    navigate(`/field/${projectId}/findings/${findingId}/edit`)
                }
                sx={{ display: 'flex', alignItems: 'stretch' }}
            >
                <CardContent sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <Typography variant="h6">
                            {findingNumber ||
                                intl.formatMessage({
                                    id: 'finding.form.newTitle'
                                })}
                            {shortDescription ? ` · ${shortDescription}` : ''}
                        </Typography>
                        {isDuplicate && (
                            <Chip
                                size="small"
                                color="warning"
                                icon={<WarningAmberIcon />}
                                label={
                                    <FormattedMessage id="finding.duplicate.badge" />
                                }
                            />
                        )}
                        {/* At the far end of the line, as in the Kenndaten
                            heading of the editors this card opens. `ml: 'auto'`
                            rather than a `justifyContent` on the row: the
                            Befundnummer and the badge belong together on the
                            left. */}
                        <FindingTypeChip
                            findingType={findingType}
                            sx={{ ml: 'auto' }}
                        />
                    </Box>
                    {subtitle && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary'
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </CardContent>
                {previewMediaId && (
                    <FieldCardPreview
                        mediaId={previewMediaId}
                        version={previewVersion}
                    />
                )}
            </CardActionArea>
            <Divider />
            <CardContent>
                <FieldFindingCardDescriptions
                    projectId={projectId}
                    findingId={findingId}
                    planums={planums}
                    profiles={profiles}
                />
            </CardContent>
        </Card>
    );
}

export default memo(FieldFindingCard);
